import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, GraduationCap, Plus, RotateCcw, Save, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Loader, Pill } from "@/components/Bits";

const adminNav = [{ to: "/admin", label: "Admin" }];

const field = "px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:border-blue-500 transition";
const TABS = [
  ["baseline", "Grade baseline"],
  ["usage", "Usage & cost"],
  ["prompts", "Default agent prompts"],
];

const SubjectEditor = ({ doc, onSaved }) => {
  const [draft, setDraft] = useState(doc);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [inputs, setInputs] = useState({});

  const chapters = draft.chapters || [];
  const topicCount = chapters.reduce((n, c) => n + (c.topics || []).length, 0);
  const val = (k) => inputs[k] || "";
  const set = (k, v) => setInputs({ ...inputs, [k]: v });

  const save = async (next = draft) => {
    setBusy(true);
    try {
      await api.put(`/admin/syllabi/${doc.syllabus_id}`, {
        grade: next.grade,
        subject: next.subject,
        chapters: next.chapters || [],
        order: next.order || 0,
      });
      toast.success(`${next.subject} saved`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${draft.subject}” with all its chapters and topics?`)) return;
    await api.delete(`/admin/syllabi/${doc.syllabus_id}`);
    toast.success("Subject deleted");
    onSaved();
  };

  const setChapter = (i, patch) => {
    const next = [...chapters];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, chapters: next });
  };

  const addChapter = (name) => {
    if (!name.trim()) return;
    setDraft({ ...draft, chapters: [...chapters, { name: name.trim(), summary: "", topics: [] }] });
    set("newChapter", "");
  };

  const removeChapter = (i) => setDraft({ ...draft, chapters: chapters.filter((_, x) => x !== i) });

  const addTopic = (i, title) => {
    if (!title.trim()) return;
    setChapter(i, { topics: [...(chapters[i].topics || []), { title: title.trim(), summary: "" }] });
    set(`t${i}`, "");
  };

  const removeTopic = (i, ti) => setChapter(i, { topics: (chapters[i].topics || []).filter((_, x) => x !== ti) });

  return (
    <div className="card-flat p-6" data-testid={`admin-syllabus-${doc.syllabus_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid={`admin-subject-name-${doc.syllabus_id}`}
          value={draft.subject}
          onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          className={`${field} max-w-xs font-semibold`}
        />
        <Pill tone="slate">grade {draft.grade}</Pill>
        <Pill tone="blue">{chapters.length} chapters · {topicCount} topics</Pill>
        <span className="ml-auto flex gap-2">
          <button
            data-testid={`admin-subject-save-${doc.syllabus_id}`}
            onClick={() => save()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button
            data-testid={`admin-subject-delete-${doc.syllabus_id}`}
            onClick={remove}
            className="p-2 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
            title="Delete subject"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {chapters.map((chapter, i) => {
          const isOpen = open[i] ?? false;
          return (
            <div key={i} className="border border-slate-200 rounded-xl">
              <div className="flex items-center gap-2 px-3 py-2.5">
                <button onClick={() => setOpen({ ...open, [i]: !isOpen })} data-testid={`admin-chapter-toggle-${i}`} className="text-slate-400">
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>
                <input
                  data-testid={`admin-chapter-name-${i}`}
                  value={chapter.name || ""}
                  onChange={(e) => setChapter(i, { name: e.target.value })}
                  className={`${field} flex-1`}
                  placeholder="Chapter name"
                />
                <span className="text-xs text-slate-400 w-16 text-right">{(chapter.topics || []).length} topics</span>
                <button
                  data-testid={`admin-chapter-remove-${i}`}
                  onClick={() => removeChapter(i)}
                  className="p-1.5 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                  title="Remove chapter"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {isOpen && (
                <div className="px-4 pb-4 space-y-2">
                  <input
                    data-testid={`admin-chapter-summary-${i}`}
                    value={chapter.summary || ""}
                    onChange={(e) => setChapter(i, { summary: e.target.value })}
                    className={`${field} w-full`}
                    placeholder="One-line summary (optional)"
                  />
                  {(chapter.topics || []).map((topic, ti) => (
                    <div key={ti} className="flex items-center gap-2 pl-4">
                      <input
                        data-testid={`admin-topic-${i}-${ti}`}
                        value={topic.title || ""}
                        onChange={(e) => {
                          const topics = [...(chapter.topics || [])];
                          topics[ti] = { ...topics[ti], title: e.target.value };
                          setChapter(i, { topics });
                        }}
                        className={`${field} flex-1`}
                      />
                      <button
                        data-testid={`admin-topic-remove-${i}-${ti}`}
                        onClick={() => removeTopic(i, ti)}
                        className="p-1.5 rounded-full text-slate-300 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2 pl-4">
                    <input
                      data-testid={`admin-topic-add-input-${i}`}
                      value={val(`t${i}`)}
                      onChange={(e) => set(`t${i}`, e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addTopic(i, val(`t${i}`))}
                      placeholder="Add a topic…"
                      className={`${field} flex-1`}
                    />
                    <button
                      data-testid={`admin-topic-add-${i}`}
                      onClick={() => addTopic(i, val(`t${i}`))}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-300 text-xs font-semibold hover:border-slate-500"
                    >
                      <Plus className="w-3.5 h-3.5" /> Topic
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex gap-2">
        <input
          data-testid="admin-chapter-add-input"
          value={val("newChapter")}
          onChange={(e) => set("newChapter", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addChapter(val("newChapter"))}
          placeholder={`Add a chapter to ${draft.subject}…`}
          className={`${field} flex-1`}
        />
        <button
          data-testid="admin-chapter-add"
          onClick={() => addChapter(val("newChapter"))}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
        >
          <Plus className="w-3.5 h-3.5" /> Chapter
        </button>
      </div>
      <p className="mt-3 text-xs text-slate-400">Changes here are local until you press Save.</p>
    </div>
  );
};

export default function AdminPortal() {
  const [tab, setTab] = useState("baseline");
  const [overview, setOverview] = useState(null);
  const [grade, setGrade] = useState("");
  const [syllabi, setSyllabi] = useState([]);
  const [prompts, setPrompts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [newGrade, setNewGrade] = useState("");
  const [newSubject, setNewSubject] = useState("");

  const loadOverview = useCallback(async () => {
    const { data } = await api.get("/admin/overview");
    setOverview(data);
    setGrade((prev) => (data.grades.includes(prev) ? prev : data.grades[0] || ""));
  }, []);

  const loadSyllabi = useCallback(async () => {
    if (!grade) return setSyllabi([]);
    const { data } = await api.get(`/admin/syllabi?grade=${grade}`);
    setSyllabi(data.syllabi);
  }, [grade]);

  const loadUsage = useCallback(async () => {
    const { data } = await api.get("/admin/usage");
    setUsage(data);
    setLimits(data.settings);
  }, []);

  const loadPrompts = useCallback(async () => {
    const { data } = await api.get("/admin/default-prompts");
    setPrompts(data);
    setDrafts(Object.fromEntries(data.agents.map((a) => [a.agent_key, a.prompt])));
  }, []);

  useEffect(() => {
    loadOverview();
    loadPrompts();
    loadUsage();
  }, [loadOverview, loadPrompts, loadUsage]);

  useEffect(() => {
    loadSyllabi();
  }, [loadSyllabi]);

  const addGrade = async () => {
    if (!newGrade.trim()) return;
    try {
      await api.post("/admin/grades", { grade: newGrade.trim() });
      toast.success(`Grade ${newGrade.trim()} added`);
      setNewGrade("");
      await loadOverview();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const deleteGrade = async (g) => {
    if (!window.confirm(`Remove grade ${g} from the platform?`)) return;
    try {
      await api.delete(`/admin/grades/${g}`);
      toast.success(`Grade ${g} removed`);
      await loadOverview();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const addSubject = async () => {
    if (!newSubject.trim()) return;
    try {
      await api.post("/admin/syllabi", { grade, subject: newSubject.trim(), chapters: [] });
      toast.success(`${newSubject.trim()} added to grade ${grade}`);
      setNewSubject("");
      await Promise.all([loadSyllabi(), loadOverview()]);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveLimits = async () => {
    try {
      await api.put("/admin/usage-settings", {
        input_per_million: Number(limits.input_per_million),
        output_per_million: Number(limits.output_per_million),
        daily_calls_per_learner: Number(limits.daily_calls_per_learner),
        monthly_calls_per_learner: Number(limits.monthly_calls_per_learner),
        reserved_interactive: Number(limits.reserved_interactive),
        platform_daily_budget_usd: Number(limits.platform_daily_budget_usd),
      });
      toast.success("Limits and pricing updated");
      await loadUsage();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const savePrompt = async (agent_key, prompt) => {
    try {
      await api.put("/admin/default-prompts", { agent_key, prompt });
      toast.success(prompt ? "Platform default updated" : "Reverted to the built-in prompt");
      loadPrompts();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!overview) return <Shell nav={adminNav}><Loader label="Loading admin portal…" /></Shell>;

  const current = overview.coverage.find((c) => c.grade === grade);

  return (
    <Shell nav={adminNav}>
      <div className="reveal" data-testid="admin-root">
        <p className="rule-label">Admin portal</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Platform control</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-3xl">
          The grade baseline is internal reference data: Grade → Subject → Chapter → Topic, curated by hand. It anchors
          the Curriculum Advisor's sense of level, depth and prerequisites. Learners never see it and it is never
          reproduced verbatim in their plans.
        </p>

        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {[
            ["Grades", overview.grades.length],
            ["Subjects", overview.total_syllabi],
            ["Students", overview.students],
          ].map(([k, v]) => (
            <div key={k} className="card-flat p-5">
              <p className="rule-label">{k}</p>
              <p className="mt-2 font-display text-2xl font-extrabold">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "baseline" && (
          <div className="mt-8">
            <div className="card-flat p-6" data-testid="admin-grades">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-blue-600" />
                <p className="rule-label">Grades</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {overview.coverage.map((c) => (
                  <div
                    key={c.grade}
                    className={`flex items-center gap-2 pl-4 pr-2 py-2 rounded-full border transition-colors ${
                      grade === c.grade ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"
                    }`}
                  >
                    <button data-testid={`admin-grade-${c.grade}`} onClick={() => setGrade(c.grade)} className="text-sm font-semibold">
                      Grade {c.grade}
                      <span className={`ml-2 text-xs font-normal ${grade === c.grade ? "text-slate-300" : "text-slate-400"}`}>
                        {c.subjects ? `${c.subjects} subj · ${c.chapters} ch · ${c.topics} top` : "not set up yet"}
                      </span>
                    </button>
                    <button
                      data-testid={`admin-grade-delete-${c.grade}`}
                      onClick={() => deleteGrade(c.grade)}
                      className={`p-1 rounded-full ${grade === c.grade ? "hover:bg-white/20" : "hover:bg-rose-50 hover:text-rose-600"}`}
                      title="Remove grade"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <input
                  data-testid="admin-grade-add-input"
                  value={newGrade}
                  onChange={(e) => setNewGrade(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addGrade()}
                  placeholder="Add a grade, e.g. 11"
                  className={`${field} max-w-xs`}
                />
                <button data-testid="admin-grade-add" onClick={addGrade} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
                  <Plus className="w-3.5 h-3.5" /> Grade
                </button>
              </div>
            </div>

            {grade && (
              <>
                <div className="mt-6 card-flat p-6" data-testid="admin-subject-add">
                  <p className="rule-label">Add a subject to grade {grade}</p>
                  <div className="mt-3 flex gap-2">
                    <input
                      data-testid="admin-subject-add-input"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addSubject()}
                      placeholder="e.g. Mathematics"
                      className={`${field} flex-1 max-w-md`}
                    />
                    <button data-testid="admin-subject-add-btn" onClick={addSubject} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                      <Plus className="w-3.5 h-3.5" /> Subject
                    </button>
                    <p className="ml-auto self-center text-sm text-slate-500">
                      {current ? `${current.subjects} subjects · ${current.chapters} chapters · ${current.topics} topics` : ""}
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  {syllabi.map((doc) => (
                    <SubjectEditor key={doc.syllabus_id} doc={doc} onSaved={() => { loadSyllabi(); loadOverview(); }} />
                  ))}
                  {!syllabi.length && <Empty title={`Grade ${grade} has no subjects yet`} note="Add a subject above, then its chapters and topics." />}
                </div>
              </>
            )}
          </div>
        )}


        {tab === "usage" && usage && limits && (
          <div className="mt-8 space-y-6" data-testid="admin-usage">
            <div className="grid sm:grid-cols-4 gap-4">
              {[
                ["Today", `$${usage.today.cost_usd}`, `${usage.today.calls} calls`],
                ["Last 7 days", `$${usage.week.cost_usd}`, `${usage.week.calls} calls`],
                ["Last 30 days", `$${usage.month.cost_usd}`, `${usage.month.calls} calls`],
                ["Projected month", `$${usage.projected_month_usd}`, `at today's rate`],
              ].map(([k, v, note]) => (
                <div key={k} className="card-flat p-5">
                  <p className="rule-label">{k}</p>
                  <p className="mt-2 font-display text-2xl font-extrabold">{v}</p>
                  <p className="mt-1 text-xs text-slate-500">{note}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="card-flat p-6">
                <p className="rule-label">Spend by agent · 30 days</p>
                <div className="mt-4 space-y-2">
                  {usage.by_agent.map((a) => (
                    <div key={a.agent_key} className="flex items-center gap-3 text-sm">
                      <span className="w-40 font-medium">{a.agent_key}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, (a.cost_usd / Math.max(0.0001, usage.by_agent[0].cost_usd)) * 100)}%` }} />
                      </div>
                      <span className="w-24 text-right text-slate-500">${a.cost_usd} · {a.calls}</span>
                    </div>
                  ))}
                  {!usage.by_agent.length && <p className="text-sm text-slate-500">No generations recorded yet.</p>}
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  {usage.month.failures} failed calls in 30 days · {usage.lockouts} account(s) currently locked out
                </p>
              </div>

              <div className="card-flat p-6">
                <p className="rule-label">Heaviest learners · 30 days</p>
                <div className="mt-4 space-y-2">
                  {usage.top_learners.map((l) => (
                    <div key={l.name} className="flex items-center justify-between text-sm">
                      <span className="font-medium truncate">{l.name}</span>
                      <span className="text-slate-500">${l.cost_usd} · {l.calls} calls</span>
                    </div>
                  ))}
                  {!usage.top_learners.length && <p className="text-sm text-slate-500">No learner activity yet.</p>}
                </div>
              </div>
            </div>

            <div className="card-flat p-6" data-testid="admin-limits">
              <p className="rule-label">Fair-use limits &amp; model pricing</p>
              <p className="mt-2 text-sm text-slate-500">
                The platform stays free, so these are the levers that keep it affordable. Interactive calls (a learner
                waiting on a card) always keep the reserved slice.
              </p>
              <div className="mt-5 grid sm:grid-cols-3 gap-4">
                {[
                  ["daily_calls_per_learner", "Daily calls / learner"],
                  ["monthly_calls_per_learner", "Monthly calls / learner"],
                  ["reserved_interactive", "Reserved for interactive"],
                  ["platform_daily_budget_usd", "Platform daily budget ($)"],
                  ["input_per_million", "Input $ / 1M tokens"],
                  ["output_per_million", "Output $ / 1M tokens"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="rule-label">{label}</label>
                    <input
                      data-testid={`admin-limit-${key}`}
                      type="number"
                      step="any"
                      className={`${field} mt-2 w-full`}
                      value={limits[key]}
                      onChange={(e) => setLimits({ ...limits, [key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              <button data-testid="admin-save-limits" onClick={saveLimits} className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
                <Save className="w-3.5 h-3.5" /> Save limits
              </button>
            </div>
          </div>
        )}

        {tab === "prompts" && prompts && (
          <div className="mt-8 space-y-4" data-testid="admin-prompts">
            <div className="card-flat p-6 bg-slate-900 text-white border-slate-800">
              <p className="rule-label !text-slate-400">Layer 1 · immutable safety layer · {prompts.model}</p>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{prompts.safety_layer}</p>
              <p className="mt-4 text-xs text-slate-400">
                Layer 2 is the platform baseline below. Guardians add layer 3 and learners aged 13+ add layer 4 on their
                own <span className="font-semibold">/agents</span> screen — those layers only ever add to what you set here.
              </p>
            </div>
            {prompts.agents.map((a) => (
              <div key={a.agent_key} className="card-flat p-6" data-testid={`admin-prompt-${a.agent_key}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-lg font-semibold">{a.role}</h3>
                  {a.customised && <Pill tone="amber">platform default overridden</Pill>}
                </div>
                <p className="mt-2 rule-label">Layer 2 · platform baseline</p>
                <textarea
                  data-testid={`admin-prompt-input-${a.agent_key}`}
                  rows={4}
                  value={drafts[a.agent_key] ?? ""}
                  onChange={(e) => setDrafts({ ...drafts, [a.agent_key]: e.target.value })}
                  className="mt-3 w-full px-4 py-3 rounded-xl border border-slate-300 text-sm leading-relaxed focus:outline-none focus:border-blue-500"
                />
                <div className="mt-3 flex gap-2">
                  <button data-testid={`admin-prompt-save-${a.agent_key}`} onClick={() => savePrompt(a.agent_key, drafts[a.agent_key])} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
                    <Save className="w-3.5 h-3.5" /> Save default
                  </button>
                  {a.customised && (
                    <button data-testid={`admin-prompt-revert-${a.agent_key}`} onClick={() => savePrompt(a.agent_key, null)} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-slate-300 text-xs font-semibold hover:border-slate-500">
                      <RotateCcw className="w-3.5 h-3.5" /> Revert
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
